import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useStaticQuery, graphql } from "gatsby";
import { useLocation } from "@gatsbyjs/reach-router";
import { CartContext } from "../context/CartProvider";
import { useAuth } from "../context/AuthProvider";
import { ALLoading } from "./ALComponents";

function parseItemsFromSearch(search, excludedVariantIds = new Set()) {
  const items = [];
  const params = new URLSearchParams(search);
  const addArrayParams = params.getAll("add[]");

  if (addArrayParams && addArrayParams.length > 0) {
    addArrayParams.forEach((param) => {
      const pairs = String(param).split(",");
      pairs.forEach((pair) => {
        const [variantIdRaw, qtyRaw] = String(pair).split(";");
        const variantId = (variantIdRaw || "").trim();
        const quantity = parseInt(qtyRaw || "1", 10) || 1;
        if (variantId && !excludedVariantIds.has(variantId)) {
          items.push({
            variant: {
              id: `gid://shopify/ProductVariant/${variantId}`,
              product: { id: "null" },
            },
            quantity,
          });
        }
      });
    });
  }
  return items;
}

function useAddToCartFromParams(enabled = true) {
  const location = useLocation();
  const { addManyProductsToCart, cart, updateCartAttributes } = useContext(CartContext);

  const mtoProductsData = useStaticQuery(graphql`
    query {
      allShopifyProduct(filter: { tags: { in: "MTO" } }) {
        edges {
          node {
            handle
            shopifyId
            variants {
              id
            }
          }
        }
      }
    }
  `);

  const excludedVariantIds = useMemo(() => {
    const ids = new Set();
    mtoProductsData.allShopifyProduct.edges.forEach((edge) => {
      edge.node.variants.forEach((v) => {
        const numericId = String(v.id).replace("gid://shopify/ProductVariant/", "");
        if (numericId) ids.add(numericId);
      });
    });
    return ids;
  }, [mtoProductsData]);

  const [state, setState] = useState({
    status: "idle", // idle | parsing | skipped | adding | success | error
    result: null,
    checkoutUrl: null,
    error: null,
  });

  const key = useMemo(
    () => (location && location.search ? `cart:addOnce:${location.search}` : ""),
    [location && location.search]
  );

  const ranRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (ranRef.current) return;
    if (!location || !location.search) return;

    const alreadyProcessed = key && sessionStorage.getItem(key);
    if (alreadyProcessed) {
      setState((s) => ({ ...s, status: "skipped" }));
      return;
    }

    ranRef.current = true;
    setState((s) => ({ ...s, status: "parsing" }));

    const items = parseItemsFromSearch(location.search, excludedVariantIds);
    if (!items.length) {
      setState((s) => ({ ...s, status: "skipped" }));
      return;
    }

    setState((s) => ({ ...s, status: "adding" }));

    (async () => {
      try {
        const res = await addManyProductsToCart({
          dataProductsToAdd: items,
        });
        if (key) sessionStorage.setItem(key, "1");

        const checkoutUrl =
          (cart && cart.checkoutUrl) || (cart && cart.data && cart.data.checkoutUrl) || null;

        const totalPrice = res.lines.reduce(
          (sum, l) => sum + (l.variant?.final_price_luxe || 0),
          0
        );

        const newCustomAttributes = [
          ...(cart.attributes || []),
          { key: "isLuxe", value: "true" },
          { key: "subscribfy_store_credits_id", value: "" },
          { key: "subscribfy_initial_store_credits", value: totalPrice.toString() },
        ];
        await updateCartAttributes({
          customAttributes: newCustomAttributes,
          customCartId: res.cartId,
        }).then(() => {
          setState({
            status: "success",
            result: res || null,
            checkoutUrl,
            error: null,
          });
        });
      } catch (e) {
        setState({
          status: "error",
          result: null,
          checkoutUrl: null,
          error: e instanceof Error ? e : new Error("addManyProductsToCart failed"),
        });
      }
    })();
  }, [
    enabled,
    addManyProductsToCart,
    key,
    excludedVariantIds,
    location,
    cart,
    updateCartAttributes,
  ]);

  return state;
}

/**
 * Adds items from ?add[]=... only once.
 * Props:
 *  - onDone (fn): optional callback receiving the raw result
 *  - enabled (bool): allow disabling (default true)
 */
export default function CartFromParamsOnce({ onDone, enabled = true }) {
  const { status, result } = useAddToCartFromParams(enabled);
  const { accountDetails } = useAuth();

  useEffect(() => {
    if (status === "success" && typeof onDone === "function") {
      onDone(result, accountDetails);
    }
  }, [status, result, onDone, accountDetails]);

  if (status === "adding") {
    return (
      <div className="fixed z-[9999999999999] bg-white flex flex-col justify-center items-center w-screen h-screen">
        <ALLoading />
        <p className="typo-h4 mt-[10px]">Working on your cart...</p>
      </div>
    );
  }
  return null;
}
